import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useFaturas, useMarcarFaturaPaga } from '@/hooks/useFaturas'
import { formatCurrency, formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import {
    Search, Filter, ChevronLeft, ChevronRight, Loader2, Receipt,
    DollarSign, Clock, AlertTriangle, CheckCircle2,
} from 'lucide-react'
import { FaturaDetailDrawer } from '@/components/admin/FaturaDetailDrawer'
import { useGerarProRata } from '@/hooks/useFaturas'

export function FaturasPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('todos')
    const [page, setPage] = useState(1)
    const [selectedFatura, setSelectedFatura] = useState<string | null>(null)

    useEffect(() => { setPageTitle('Faturas') }, [setPageTitle])

    const { data: result, isLoading } = useFaturas({ search, status: statusFilter, page, pageSize: 12 })
    const marcarPaga = useMarcarFaturaPaga()
    const gerarProRata = useGerarProRata()

    const faturas = result?.data || []
    const totalPages = result?.totalPages || 1

    // Financial KPIs
    const allFaturas = result?.data || []
    const totalRecebido = allFaturas.filter(f => f.status === 'paga').reduce((s, f) => s + Number(f.valor), 0)
    const totalPendente = allFaturas.filter(f => f.status === 'pendente').reduce((s, f) => s + Number(f.valor), 0)
    const totalAtrasado = allFaturas.filter(f => f.status === 'atrasada').reduce((s, f) => s + Number(f.valor), 0)

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h2 className="text-xl font-bold text-surface-900">Faturas</h2>
                <p className="text-sm text-surface-700/60 mt-0.5">Gestão financeira de faturas e pagamentos</p>
            </div>

            {/* Financial KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="glass-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-surface-700/60">Recebido</p>
                        <div className="w-9 h-9 rounded-xl bg-success-50 flex items-center justify-center">
                            <CheckCircle2 size={18} className="text-success-600" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-success-600">{formatCurrency(totalRecebido)}</p>
                </div>
                <div className="glass-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-surface-700/60">Pendente</p>
                        <div className="w-9 h-9 rounded-xl bg-warning-50 flex items-center justify-center">
                            <Clock size={18} className="text-warning-600" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-warning-600">{formatCurrency(totalPendente)}</p>
                </div>
                <div className="glass-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-surface-700/60">Atrasado</p>
                        <div className="w-9 h-9 rounded-xl bg-danger-50 flex items-center justify-center">
                            <AlertTriangle size={18} className="text-danger-600" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-danger-600">{formatCurrency(totalAtrasado)}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="glass-card p-4 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <input type="text" value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                        placeholder="Buscar por associado..."
                        className="input pl-10" />
                </div>
                <div className="relative">
                    <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                        className="input pl-9 pr-10 min-w-[160px]">
                        <option value="todos">Todos os status</option>
                        <option value="pendente">Pendente</option>
                        <option value="paga">Paga</option>
                        <option value="atrasada">Atrasada</option>
                        <option value="cancelada">Cancelada</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="glass-card overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : faturas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Receipt size={40} className="text-surface-700/20 mb-3" />
                        <p className="text-surface-700/60">Nenhuma fatura encontrada</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Associado</th>
                                    <th>Veículo</th>
                                    <th>Valor</th>
                                    <th>Vencimento</th>
                                    <th>Pagamento</th>
                                    <th>Tipo</th>
                                    <th>Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {faturas.map((f) => (
                                    <tr key={f.id} className="hover:bg-surface-50 transition-colors cursor-pointer"
                                        onClick={() => setSelectedFatura(f.id)}>
                                        <td className="font-medium text-surface-900 text-sm">{f.associado_nome || '—'}</td>
                                        <td className="font-mono text-sm text-surface-700">{f.contrato_ref || '—'}</td>
                                        <td className="font-semibold text-surface-900">{formatCurrency(f.valor)}</td>
                                        <td className="text-sm text-surface-700">{formatDate(f.data_vencimento)}</td>
                                        <td className="text-sm text-surface-700">{f.data_pagamento ? formatDate(f.data_pagamento) : '—'}</td>
                                        <td><span className="text-xs capitalize text-surface-700/60">{f.tipo.replace('_', ' ')}</span></td>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(f.status)}`}>
                                                {translateStatus(f.status)}
                                            </span>
                                        </td>
                                        <td className="text-right" onClick={(e) => e.stopPropagation()}>
                                            {(f.status === 'pendente' || f.status === 'atrasada') && (
                                                <button
                                                    onClick={() => marcarPaga.mutate(f.id)}
                                                    disabled={marcarPaga.isPending}
                                                    className="text-xs px-3 py-1.5 rounded-lg border border-success-200 text-success-600 hover:bg-success-50 transition-colors gap-1 inline-flex items-center"
                                                >
                                                    <DollarSign size={13} /> Pagar
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-surface-100">
                        <p className="text-sm text-surface-700/60">Página {page} de {totalPages} — {result?.total} faturas</p>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30"><ChevronRight size={16} /></button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Drawer */}
            <FaturaDetailDrawer
                fatura={selectedFatura ? (faturas.find(f => f.id === selectedFatura) ?? null) : null}
                onClose={() => setSelectedFatura(null)}
                onMarcarPaga={(id) => { marcarPaga.mutate(id); setSelectedFatura(null) }}
                onGerarProRata={(id) => gerarProRata.mutate(id)}
            />
        </div>
    )
}
